import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { Row, Col } from 'antd';
import { useMediaQuery } from 'react-responsive';

import { useI18n } from 'context/i18n';
import { useErrorContext } from 'context/error';
import { HeadSEO } from 'components/base';
import { ProductPurchaseCol } from 'components/productPurchaseCol';

import { getLang, getIsMobile, getRealID, getMarket } from 'utils/helpers';
import { getDomainValue } from 'utils/domain';

const ProductsRows = dynamic(() => import('components/ProductsRow'));
const Product = dynamic(() => import('components/product'));
const PdpDynamicComponent = getDomainValue({
  nnormal: dynamic(() => import('@nnormal/react-web-components').then((mod) => mod.MOLECULES.PdpDynamicComponent)),
  camper: dynamic(() => import('@camper/react-web-components').then((mod) => mod.MOLECULES.PdpDynamicComponent)),
  camperlab: dynamic(() => import('@camper/react-web-components').then((mod) => mod.MOLECULES.PdpDynamicComponent)),
});
const SeoContent = dynamic(() => import('components/seo-content').then((mod) => mod.SeoContent));

const slots = {
  products: ProductsRows,
  product: Product,
};

const ProductSheetRoku = ({ productSheet, metatags, pdpProps = null, seoContent = null }) => {
  const { locale } = useI18n();
  const { spreadError } = useErrorContext();
  const isMobile = getIsMobile();
  const router = useRouter();
  const { query } = router;
  const { target, product } = query;
  const averageRate = productSheet.rate ? Math.round(productSheet.rate * 10) / 10 : null;
  const hasReviews = !productSheet.emptyReviews;
  const lang = getLang(locale);
  const market = getMarket(locale);
  const [isDimReady, setIsDimReady] = useState(false);

  const handleChangeMediaQuery = (matches) => {
    const container = document.getElementById('dimContainer');

    if (matches) {
      const calculatedHeight = container.clientHeight;
      const calculatedWidth = container.clientWidth;

      if (calculatedHeight < 500 && calculatedWidth > 500) {
        container.style.minHeight = '500px';
      }
    } else {
      container.style.minHeight = '0px';
    }
  };

  const desktopWidth = useMediaQuery(
    {
      minWidth: 1279,
    },
    undefined,
    handleChangeMediaQuery,
  );
  const mobileWidth = useMediaQuery({
    maxWidth: 767,
  });

  const targetRealID = getRealID('target', target);
  const familyRealID = getRealID('product', product);

  const gtmProps = {
    target,
    product,
    productSheet,
    targetRealID,
    familyRealID,
  };

  useEffect(() => {
    if (window?.customDIM) {
      const emptyFunc = () => undefined;
      // entiendo que esto estará en otro nodo y no en "camperone"
      try {
        const container = document.getElementById('dimContainer');

        if (container) {
          const isIphone = window?.navigator?.userAgent?.includes('iPhone');
          if (desktopWidth) {
            const calculatedHeight = container.clientHeight;
            const calculatedWidth = container.clientWidth;

            if (calculatedHeight < 500 && calculatedWidth > 500) {
              container.style.minHeight = '500px';
            }
          } else {
            container.style.minHeight = '0px';
            if (mobileWidth && isIphone) {
              container.style.maxHeight = '500px';
            }
          }
        }

        window.customDIM.install(
          'dimContainer',
          productSheet.camperone.jsonConfig,
          lang,
          () => {
            setIsDimReady(true);
          },
          emptyFunc,
        );
      } catch (e) {
        console.error(e);
        spreadError(e);
      }
    }
  }, []);

  const headScripts = [
    // según la tarea de ROKU el script es el mismo
    <script
      key="customDim"
      type="text/javascript"
      src={`https://dimtech.es/CustomDIM/${process.env.ENVIRONMENT === 'integra' ? 'Camper-pruebas' : 'Camper'}/testIFrame/js/customDIM.js`}
    ></script>,
  ];

  return (
    <div>
      <HeadSEO scripts={headScripts} />
      <Row>
        <Col xs={24} md={24} lg={16}>
          <div id="dimContainer"></div>
        </Col>
        <ProductPurchaseCol
          averageRate={averageRate}
          hasReviews={hasReviews}
          locale={locale}
          market={market}
          product={product}
          productSheet={productSheet}
          isCamperOne /* habrá que cambiar esto para que no sea vea el branding de CamperONE */
          isRoku
          isFullScreen={false}
          target={target}
          isMobile={isMobile}
          columnSizes={{
            xs: 24,
            md: 24,
            lg: 8,
          }}
          isDimReady={isDimReady}
        />
      </Row>
      {PdpDynamicComponent !== null && pdpProps !== null && pdpProps?.top !== null && (
        <PdpDynamicComponent market={pdpProps.market} data={pdpProps.top} slots={slots} />
      )}
      {PdpDynamicComponent !== null && pdpProps !== null && pdpProps?.bottom !== null && (
        <PdpDynamicComponent market={pdpProps.market} data={pdpProps.bottom} slots={slots} />
      )}
      {seoContent !== null && <SeoContent content={seoContent} camperlab={productSheet.camperlab === true} />}
    </div>
  );
};

ProductSheetRoku.propTypes = {
  productSheet: PropTypes.object,
  metatags: PropTypes.object,
};

export default ProductSheetRoku;
